import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Drawer,
  Fab,
  Menu,
  MenuItem,
  ListItemIcon,
  useMediaQuery,
  useTheme,
  BottomNavigation,
  BottomNavigationAction,
  Typography,
  IconButton,
  alpha,
} from "@mui/material";
import {
  Add as AddIcon,
  FileUpload as UploadIcon,
  CreateNewFolder as NewFolderIcon,
  FolderOpenTwoTone as FolderIcon,
  DraftsTwoTone as DraftIcon,
  PermContactCalendarTwoTone as ContactIcon,
  Menu as MenuIcon,
  BookmarkTwoTone as BookmarkIcon,
  Close,
} from "@mui/icons-material";
// カスタムフックとコンポーネントのインポート
import { useFileManager } from "../hooks/useFileManager.ts";
import { Sidebar } from "./Sidebar.tsx";
import { MainContent } from "./MainContent.tsx";
import { DetailPanel } from "./DetailPanel.tsx";
import { LoadingOverlay } from "./loadingOverlay.tsx";
import { SimpleAlertDialog } from "./SimpleAlertDialog.tsx";
import { TrashContent, TrashFolderDto } from "./TrashContent.tsx";
import { FilePreviewer } from "./FilePreviewer.tsx";
import { BookmarkedContent, BookmarkedItemDto } from "./StarredContent.tsx";
import { SharedFilesContent, MyFileItemDto } from "./SharedFilesContent.tsx";
import { RoomSendFilesContent } from "./RoomSendFilesContent.tsx";
import { AdminContent } from "./AdminContent.tsx";
import { PermissionDialog } from "./PermissionDialog.tsx";
import { FolderPickerDialog } from "./FolderPickerDialog.tsx";
import { UploadProgressWidget } from "./UploadProgressWidget.tsx";
import { HelpDrawer } from "./HelpDrawer.tsx";
import {
  AdminUserListResponse,
  AdminRoomListResponse,
  ManagedFacilityDto,
} from "../types/types.ts";
import { getMyPaletteColor } from "../utils/colorUtils.ts";
import { PaletteColorHex } from "../types/PaletteColor.ts";

// --- バックエンドの DTO と一致する型定義 ---
export interface FileFolderDto {
  folderId: string;
  folderName: string;
  isLocked: boolean;
  isBookmarked: boolean;
  isRestricted: boolean;
  myAccessLevel: number | null; // null=制限なし, 1=閲覧者, 2=編集者
  createdAt: string;
}

export interface FileItemDto {
  fileId: string;
  fileName: string;
  fileSize: number;
  contentType: string;
  fileExtension: string;
  isLocked: boolean;
  isBookmarked: boolean;
  thumbnailStatus: number; // 0:未生成, 1:生成済, 2:非対応・生成失敗
  updatedAt: string;
  createdBy: number;
  uploaderName: string;
  uploaderDept: string;
}

export interface StorageStatusDto {
  usedStorageBytes: number;
  maxStorageBytes: number;
  userUsedBytes: number;
  userQuotaBytes: number;
  roomUsedBytes: number | null;
  roomQuotaBytes: number | null;
}

export interface BreadcrumbDto {
  id: string | null;
  name: string;
}

export interface FileListResponse {
  folders: FileFolderDto[];
  files: FileItemDto[];
  breadcrumbs: BreadcrumbDto[];
  storage: StorageStatusDto;
  isAdmin: boolean;
  currentFolderAccessLevel: number | null; // null=制限なし, 1=閲覧者, 2=編集者
  roomName: string;
}

// 権限関連の型定義
export interface FolderPermissionDto {
  honbuId: number;
  name: string;
  accessLevel: number;
}

export interface FolderPermissionListResponse {
  isRestricted: boolean;
  permissions: FolderPermissionDto[];
}

export interface RoomMemberDto {
  honbuId: number;
  name: string;
}

export interface SearchFileItem extends FileItemDto {
  folderPath: string;
  folderId: string | null;
}

export interface SearchFolderItem {
  folderId: string;
  folderName: string;
  isRestricted: boolean;
  isBookmarked: boolean;
  parentPath: string;
  parentFolderId: string | null;
}

export interface SearchResponse {
  files: SearchFileItem[];
  folders: SearchFolderItem[];
}

interface FileManagerProps {
  hpcode: number;
  roomId: number;
  honbuId: number;
}

interface TrashListResponse {
  files: FileItemDto[];
  folders: TrashFolderDto[];
}

const FileManager: React.FC<FileManagerProps> = ({
  hpcode,
  roomId,
  honbuId,
}) => {
  // --- モバイル・画面幅判定 ---
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isWide = useMediaQuery("(min-width: 1300px)");
  const isDarkMode = theme.palette.mode === "dark";

  // モバイル用: サイドバーDrawer
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // ヘルプパネル
  const [helpOpen, setHelpOpen] = useState(false);

  // モバイル用: FABメニュー
  const [fabAnchorEl, setFabAnchorEl] = useState<null | HTMLElement>(null);
  const isFabMenuOpen = Boolean(fabAnchorEl);

  // --- 状態管理 ---
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "thumbnail" | "list">(
    () => {
      const saved = localStorage.getItem("chatisfiles-viewMode");
      return saved === "list" || saved === "thumbnail" ? saved : "grid";
    },
  );

  // viewMode変更時にローカルストレージに保存
  const handleViewModeChange = (mode: "grid" | "thumbnail" | "list") => {
    setViewMode(mode);
    localStorage.setItem("chatisfiles-viewMode", mode);
  };
  const [selectedFile, setSelectedFile] = useState<FileItemDto | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // フォルダピッカー用
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);
  const [folderPickerMode, setFolderPickerMode] = useState<"move" | "copy">(
    "move",
  );
  const [folderPickerFile, setFolderPickerFile] = useState<FileItemDto | null>(
    null,
  );

  // マイフォルダ用の独立したフォルダID管理
  const [myFolderCurrentFolderId, setMyFolderCurrentFolderId] = useState<
    string | null
  >(null);

  // --- カスタムフックによるデータ取得 ---
  // 部屋の共有フォルダ用
  const { data, isLoading, refresh } = useFileManager(
    hpcode,
    roomId,
    honbuId,
    currentFolderId,
  );

  // マイフォルダ用 (roomId=0)
  const {
    data: myFolderData,
    isLoading: isMyFolderLoading,
    refresh: refreshMyFolder,
  } = useFileManager(hpcode, 0, honbuId, myFolderCurrentFolderId);

  // カラー設定
  const palette = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return getMyPaletteColor(params.get("color"));
  }, []);

  // --- ハンドラー: アップロード ---
  const handleUploadFiles = async (files: File[], forMyFolder = false) => {
    if (files.length === 0) return;

    const effectiveRoomId = forMyFolder ? 0 : roomId;
    const effectiveFolderId = forMyFolder
      ? myFolderCurrentFolderId
      : currentFolderId;

    setUploadProgress({ total: files.length, current: 0, visible: true });

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append("file", file);
      formData.append("hpcode", hpcode.toString());
      formData.append("roomId", effectiveRoomId.toString());
      formData.append("honbuId", honbuId.toString());
      if (effectiveFolderId) {
        formData.append("folderId", effectiveFolderId);
      }

      try {
        const res = await fetch(`/api/files/upload?honbuId=${honbuId}`, {
          method: "POST",
          body: formData,
        });
        if (!res.ok) {
          const errorText = await res.text();
          showToast(`アップロード失敗: ${file.name} ${errorText}`, "error");
        }
      } catch (err) {
        console.error("Upload error:", err);
      }
      setUploadProgress((prev) => ({ ...prev, current: i + 1 }));
    }
    if (forMyFolder) {
      refreshMyFolder();
    } else {
      refresh();
    }
  };

  // --- ハンドラー: ブックマーク切り替え ---
  const handleToggleBookmark = async (id: string, type: "folder" | "file") => {
    try {
      await fetch(`/api/files/items/bookmark-toggle?honbuId=${honbuId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, type, honbuId }),
      });
      if (currentView === "myFolder") {
        refreshMyFolder();
      } else {
        refresh();
      }
    } catch (err) {
      console.error("Bookmark toggle error:", err);
    }
  };

  // --- ハンドラー: 論理削除 ---
  const handleDeleteItem = async (id: string, type: "folder" | "file") => {
    try {
      const res = await fetch(
        `/api/files/items/${id}/soft-delete?honbuId=${honbuId}`,
        {
          method: "PATCH",
        },
      );
      if (res.ok) {
        setIsDetailOpen(false);
        refresh();
      } else {
        showToast(await res.text(), "error");
      }
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  // 詳細パネル用 (iボタン)
  const [selectedDetailFile, setSelectedDetailFile] =
    useState<FileItemDto | null>(null);

  // プレビュー用 (ファイルクリック)
  const [previewFile, setPreviewFile] = useState<FileItemDto | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewRoomId, setPreviewRoomId] = useState<number>(0);

  // --- 一括選択モード ---
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<
    Array<{ id: string; type: "folder" | "file" }>
  >([]);
  // Shift 押下で仮起動した選択モードかどうか（true の間に Shift を離すと解除）
  const isShiftSelectingRef = useRef(false);

  const handleEnterSelectionMode = () => {
    setIsSelectionMode(true);
    setSelectedItems([]);
  };

  const handleExitSelectionMode = () => {
    setIsSelectionMode(false);
    setSelectedItems([]);
    isShiftSelectingRef.current = false;
  };

  const handleToggleSelection = (id: string, type: "folder" | "file") => {
    // Shift 仮起動中に1件でも選択操作をしたら、Shift を離してもモードを継続する
    isShiftSelectingRef.current = false;
    setSelectedItems((prev) => {
      const exists = prev.some((item) => item.id === id);
      if (exists) return prev.filter((item) => item.id !== id);
      return [...prev, { id, type }];
    });
  };

  const handleBulkDownload = async () => {
    if (selectedItems.length === 0) return;
    try {
      const res = await fetch(`/api/files/batch/download?honbuId=${honbuId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: selectedItems, honbuId }),
      });
      if (!res.ok) {
        const errorText = await res.text();
        showToast(`ダウンロード失敗: ${errorText}`, "error");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disposition = res.headers.get("Content-Disposition");
      const match = disposition?.match(/filename=(.+)/);
      a.download = match?.[1] || "download.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast("ダウンロードを開始しました", "success");
      handleExitSelectionMode();
    } catch (err) {
      console.error("Batch download error:", err);
      showToast("通信エラーが発生しました", "error");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    // 確認は ConfirmDialog を使う
    setBulkDeleteConfirmOpen(true);
  };

  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);

  // --- 権限設定ダイアログ用 State ---
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [permissionTarget, setPermissionTarget] = useState<{
    folderId: string;
    folderName: string;
    isRestricted: boolean;
  } | null>(null);

  const handleOpenPermissionDialog = (
    folderId: string,
    folderName: string,
    isRestricted: boolean,
  ) => {
    setPermissionTarget({ folderId, folderName, isRestricted });
    setPermissionDialogOpen(true);
  };

  const handleToggleRestriction = async (folderId: string) => {
    try {
      const res = await fetch(
        `/api/files/folders/${folderId}/toggle-restriction?honbuId=${honbuId}`,
        { method: "PATCH" },
      );
      if (res.ok) {
        const data = await res.json();
        setPermissionTarget((prev) =>
          prev ? { ...prev, isRestricted: data.isRestricted } : null,
        );
        showToast(
          data.isRestricted
            ? "アクセス制限をONにしました"
            : "アクセス制限を解除しました",
          "success",
        );
        if (currentView === "myFolder") {
          refreshMyFolder();
        } else {
          refresh();
        }
      } else {
        showToast(await res.text(), "error");
      }
    } catch (err) {
      console.error(err);
      showToast("通信エラーが発生しました", "error");
    }
  };

  const executeBulkDelete = async () => {
    try {
      const res = await fetch(`/api/files/batch/soft-delete?honbuId=${honbuId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: selectedItems, honbuId }),
      });
      if (res.ok) {
        const data = await res.json();
        showToast(`${data.deletedCount}件をゴミ箱へ移動しました`, "success");
        handleExitSelectionMode();
        if (currentView === "myFolder") {
          refreshMyFolder();
        } else {
          refresh();
        }
      } else {
        showToast(`削除失敗: ${await res.text()}`, "error");
      }
    } catch (err) {
      console.error("Batch delete error:", err);
      showToast("通信エラーが発生しました", "error");
    }
  };

  // --- ハンドラー: ファイルクリック（プレビュー表示） ---
  const handleFileClick = (file: FileItemDto, overrideRoomId?: number) => {
    setPreviewFile(file);
    setPreviewRoomId(overrideRoomId ?? roomId);
    setIsPreviewOpen(true);
  };

  // --- ハンドラー: 詳細ボタンクリック（詳細パネル表示） ---
  const handleShowDetail = (file: FileItemDto) => {
    setSelectedDetailFile(file);
    setIsDetailOpen(true);
  };

  // ダイアログの状態管理
  const [isInputDialogOpen, setIsInputDialogOpen] = useState(false);
  const [inputMode, setInputMode] = useState<"createFolder" | "rename">(
    "createFolder",
  );
  const [inputValue, setInputValue] = useState("");
  const [targetItem, setTargetItem] = useState<{
    id: string;
    type: "folder" | "file";
  } | null>(null);

  // ダイアログを開く処理 (フォルダ作成用)
  const openCreateFolderDialog = () => {
    setInputMode("createFolder");
    setInputValue("");
    setIsInputDialogOpen(true);
  };

  // ダイアログを開く処理 (名前変更用)
  const openRenameDialog = (
    id: string,
    currentName: string,
    type: "folder" | "file",
  ) => {
    setInputMode("rename");
    setInputValue(currentName);
    setTargetItem({ id, type });
    setIsInputDialogOpen(true);
  };

  // 実行ボタンが押された時の処理
  const handleSubmit = async () => {
    // 入力が空の場合は何もしない
    if (!inputValue.trim()) return;

    try {
      let res: Response | undefined;

      if (inputMode === "createFolder") {
        // --- フォルダ作成モード ---
        const isMyFolderView = currentView === "myFolder";

        // サーバー側の Guid? 型に合わせて、空文字やundefinedの場合は null を送る
        const payload = {
          hpcode,
          roomId: isMyFolderView ? 0 : roomId,
          honbuId,
          parentFolderId:
            (isMyFolderView ? myFolderCurrentFolderId : currentFolderId) ||
            null,
          folderName: inputValue,
        };

        res = await fetch(`/api/files/folders?honbuId=${honbuId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else if (inputMode === "rename" && targetItem) {
        // --- 名前変更モード ---

        // サーバー側のクラス定義に合わせてオブジェクト形式 { newName: "..." } で送る
        const payload = { newName: inputValue };

        res = await fetch(
          `/api/files/items/${targetItem.id}/rename?type=${targetItem.type}&honbuId=${honbuId}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );
      }

      // --- 結果判定 ---
      if (res && res.ok) {
        // 成功時
        showToast(
          inputMode === "createFolder"
            ? "フォルダを作成しました"
            : "名前を変更しました",
          "success",
        );
        setIsInputDialogOpen(false);
        setInputValue(""); // 入力をクリア
        if (currentView === "myFolder") {
          refreshMyFolder();
        } else {
          refresh();
        }
      } else {
        // 失敗時（サーバーからのエラーメッセージを取得して表示）
        const errorText = res ? await res.text() : "不明なエラーが発生しました";
        console.error("API Error:", errorText);
        showToast(`エラー: ${errorText}`, "error");
      }
    } catch (err) {
      console.error("Operation failed:", err);
      showToast("通信エラーが発生しました", "error");
    }
  };

  // --- 追加: トースト通知用 State ---
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const showToast = (
    message: string,
    severity: "success" | "error" = "success",
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // --- アップロード進捗 ---
  const [uploadProgress, setUploadProgress] = useState<{
    total: number;
    current: number;
    visible: boolean;
  }>({ total: 0, current: 0, visible: false });

  // --- 追加: 削除確認ダイアログ用 State ---
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    type: "folder" | "file";
  } | null>(null);

  // 1. 削除ボタンが押された時の処理（ダイアログを開く）
  const handleDeleteClick = (id: string, type: "folder" | "file") => {
    setDeleteTarget({ id, type });
    setDeleteConfirmOpen(true);
  };

  // 2. ダイアログで「削除」が押された時の実行処理
  const executeDelete = async () => {
    if (!deleteTarget) return;

    try {
      // APIパスとパラメータを修正
      const res = await fetch(
        `/api/files/items/${deleteTarget.id}/soft-delete?type=${deleteTarget.type}&honbuId=${honbuId}`,
        {
          method: "PATCH",
        },
      );

      if (res.ok) {
        showToast("ゴミ箱へ移動しました", "success"); // 右下に通知
        setIsDetailOpen(false); // 詳細パネルが開いていれば閉じる
        if (currentView === "myFolder") {
          refreshMyFolder();
        } else {
          refresh();
        }
      } else {
        const errorText = await res.text();
        showToast(`削除失敗: ${errorText}`, "error");
      }
    } catch (err) {
      console.error("Delete error:", err);
      showToast("ネットワークエラーが発生しました", "error");
    }
  };

  const [currentView, setCurrentView] = useState<
    | "files"
    | "trashShared"
    | "trashMyFolder"
    | "bookmarked"
    | "myFolder"
    | "sharedFiles"
    | "roomSendFiles"
    | "admin"
  >(roomId === 0 ? "myFolder" : "files");

  // ビュー切替時に選択モード解除
  useEffect(() => {
    handleExitSelectionMode();
  }, [currentView]);

  // ? キー: ヘルプパネルのトグル（INPUT/TEXTAREA フォーカス中は無視）
  // postMessage: 親ウィンドウから openHelp メッセージでオープン
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "?" &&
        !["INPUT", "TEXTAREA"].includes((e.target as HTMLElement).tagName)
      ) {
        setHelpOpen((prev) => !prev);
      }
    };
    const handleMessage = (e: MessageEvent) => {
      if (e.data?.type === "openHelp") {
        setHelpOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // Shift キー: 一括選択モードの一時起動
  // 押下でモード開始 → 未選択のまま離すと解除 / 1件以上選択済みなら離してもモード継続
  useEffect(() => {
    // 一括選択モードが使えるビューのみ有効
    if (currentView !== "files" && currentView !== "myFolder") return;

    const handleShiftDown = (e: KeyboardEvent) => {
      if (e.key !== "Shift" || e.repeat) return;
      // 入力欄フォーカス中（大文字入力等）やプレビュー表示中は発動しない
      if (["INPUT", "TEXTAREA"].includes((e.target as HTMLElement).tagName))
        return;
      if (isPreviewOpen || isSelectionMode) return;
      isShiftSelectingRef.current = true;
      handleEnterSelectionMode();
    };
    const cancelShiftSelection = () => {
      if (isShiftSelectingRef.current) {
        handleExitSelectionMode();
      }
    };
    const handleShiftUp = (e: KeyboardEvent) => {
      if (e.key === "Shift") cancelShiftSelection();
    };
    window.addEventListener("keydown", handleShiftDown);
    window.addEventListener("keyup", handleShiftUp);
    // Shift 押下中にウィンドウを離れると keyup を拾えないため blur でも解除する
    window.addEventListener("blur", cancelShiftSelection);
    return () => {
      window.removeEventListener("keydown", handleShiftDown);
      window.removeEventListener("keyup", handleShiftUp);
      window.removeEventListener("blur", cancelShiftSelection);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, isSelectionMode, isPreviewOpen]);

  // ゴミ箱データ用State（共有フォルダ）
  const [sharedTrashData, setSharedTrashData] =
    useState<TrashListResponse | null>(null);
  const [isSharedTrashLoading, setIsSharedTrashLoading] = useState(false);

  const fetchSharedTrash = async () => {
    setIsSharedTrashLoading(true);
    try {
      const res = await fetch(
        `/api/files/trash?hpcode=${hpcode}&roomId=${roomId}&honbuId=${honbuId}`,
      );
      if (res.ok) setSharedTrashData(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setIsSharedTrashLoading(false);
    }
  };

  // ゴミ箱データ用State（マイフォルダ）
  const [myTrashData, setMyTrashData] = useState<TrashListResponse | null>(
    null,
  );
  const [isMyTrashLoading, setIsMyTrashLoading] = useState(false);

  const fetchMyFolderTrash = async () => {
    setIsMyTrashLoading(true);
    try {
      const res = await fetch(
        `/api/files/trash?hpcode=${hpcode}&roomId=0&honbuId=${honbuId}`,
      );
      if (res.ok) setMyTrashData(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setIsMyTrashLoading(false);
    }
  };

  // --- ハンドラー: ゴミ箱から復元 ---
  const handleTrashRestore = async (id: string, type: "file" | "folder") => {
    try {
      const res = await fetch(
        `/api/files/items/${id}/restore?type=${type}&honbuId=${honbuId}`,
        { method: "PATCH" },
      );
      if (res.ok) {
        showToast("復元しました", "success");
        if (currentView === "trashShared") fetchSharedTrash();
        else fetchMyFolderTrash();
      } else {
        const text = await res.text();
        showToast(text || "復元に失敗しました", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("復元に失敗しました", "error");
    }
  };

  // --- ハンドラー: ゴミ箱から完全削除 ---
  const handleTrashHardDelete = async (id: string, type: "file" | "folder") => {
    try {
      const res = await fetch(
        `/api/files/items/${id}/hard-delete?type=${type}&honbuId=${honbuId}`,
        { method: "DELETE" },
      );
      if (res.ok || res.status === 204) {
        showToast("完全に削除しました", "success");
        if (currentView === "trashShared") fetchSharedTrash();
        else fetchMyFolderTrash();
      } else {
        const text = await res.text();
        showToast(text || "削除に失敗しました", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("削除に失敗しました", "error");
    }
  };

  // --- ハンドラー: ゴミ箱 一括復元 ---
  const handleTrashBatchRestore = async (items: { id: string; type: "file" | "folder" }[]) => {
    try {
      const res = await fetch(`/api/files/batch/restore?honbuId=${honbuId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: items.map((i) => ({ id: i.id, type: i.type })), honbuId }),
      });
      if (res.ok) {
        showToast(`${items.length}件を復元しました`, "success");
        if (currentView === "trashShared") fetchSharedTrash();
        else fetchMyFolderTrash();
      } else {
        const text = await res.text();
        showToast(text || "復元に失敗しました", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("復元に失敗しました", "error");
    }
  };

  // --- ハンドラー: ゴミ箱 一括物理削除 ---
  const handleTrashBatchHardDelete = async (items: { id: string; type: "file" | "folder" }[]) => {
    try {
      const res = await fetch(`/api/files/batch/hard-delete?honbuId=${honbuId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: items.map((i) => ({ id: i.id, type: i.type })), honbuId }),
      });
      if (res.ok || res.status === 204) {
        showToast(`${items.length}件を完全に削除しました`, "success");
        if (currentView === "trashShared") fetchSharedTrash();
        else fetchMyFolderTrash();
      } else {
        const text = await res.text();
        showToast(text || "削除に失敗しました", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("削除に失敗しました", "error");
    }
  };

  // --- ハンドラー: ファイルのコピー ---
  const handleCopyFile = async (fileId: string) => {
    try {
      // ローディング表示などを出しても良いですが、ここではシンプルに実行
      const res = await fetch(
        `/api/files/items/${fileId}/copy?honbuId=${honbuId}&roomId=${roomId}`,
        {
          method: "POST",
        },
      );

      if (res.ok) {
        showToast("コピーを作成しました", "success");
        setIsDetailOpen(false); // パネルを閉じる
        if (currentView === "myFolder") {
          refreshMyFolder();
        } else {
          refresh();
        }
      } else {
        const errorText = await res.text();
        showToast(`コピー失敗: ${errorText}`, "error");
      }
    } catch (err) {
      console.error("Copy error:", err);
      showToast("通信エラーが発生しました", "error");
    }
  };

  // 管理者画面データ用
  const [adminData, setAdminData] = useState<AdminUserListResponse | null>(
    null,
  );
  const [adminRoomData, setAdminRoomData] =
    useState<AdminRoomListResponse | null>(null);
  const [isAdminLoading, setIsAdminLoading] = useState(false);

  // 管理対象施設の切り替え（自施設 + 子孫施設。デフォルトは自施設）
  const [adminHpcode, setAdminHpcode] = useState(hpcode);
  const [managedFacilities, setManagedFacilities] = useState<
    ManagedFacilityDto[]
  >([]);

  const fetchManagedFacilities = async () => {
    try {
      const res = await fetch(
        `/api/files/admin/managed-facilities?honbuId=${honbuId}`,
      );
      if (res.ok) {
        const data = await res.json();
        setManagedFacilities(data.facilities);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAdminData = async (targetHpcode: number = adminHpcode) => {
    setIsAdminLoading(true);
    try {
      const [usersRes, roomsRes] = await Promise.all([
        fetch(
          `/api/files/admin/users?hpcode=${targetHpcode}&honbuId=${honbuId}`,
        ),
        fetch(
          `/api/files/admin/rooms?hpcode=${targetHpcode}&honbuId=${honbuId}`,
        ),
      ]);
      if (usersRes.ok) setAdminData(await usersRes.json());
      if (roomsRes.ok) setAdminRoomData(await roomsRes.json());
    } catch (err) {
      console.error(err);
    } finally {
      setIsAdminLoading(false);
    }
  };

  const handleAdminFacilityChange = (newHpcode: number) => {
    setAdminHpcode(newHpcode);
    fetchAdminData(newHpcode);
  };

  const handleUpdateFacilityQuota = async (
    newQuotaBytes: number,
  ): Promise<boolean> => {
    try {
      const res = await fetch(`/api/files/admin/facility-quota?honbuId=${honbuId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          hpcode: adminHpcode,
          newDefaultQuotaBytes: newQuotaBytes,
        }),
      });
      if (res.ok) {
        showToast("共通上限を変更しました", "success");
        return true;
      } else {
        const errorText = await res.text();
        showToast(`エラー: ${errorText}`, "error");
        return false;
      }
    } catch (err) {
      console.error(err);
      showToast("通信エラーが発生しました", "error");
      return false;
    }
  };

  const handleUpdateUserQuota = async (
    targetHonbuId: number,
    newQuotaBytes: number,
  ): Promise<boolean> => {
    try {
      const res = await fetch(`/api/files/admin/user-quota?honbuId=${honbuId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          hpcode: adminHpcode,
          honbuId: targetHonbuId,
          newQuotaBytes,
        }),
      });
      if (res.ok) {
        showToast("個人上限を変更しました", "success");
        return true;
      } else {
        const errorText = await res.text();
        showToast(`エラー: ${errorText}`, "error");
        return false;
      }
    } catch (err) {
      console.error(err);
      showToast("通信エラーが発生しました", "error");
      return false;
    }
  };

  const handleUpdateDefaultRoomQuota = async (
    newQuotaBytes: number,
  ): Promise<boolean> => {
    try {
      const res = await fetch(`/api/files/admin/default-room-quota?honbuId=${honbuId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          hpcode: adminHpcode,
          newDefaultQuotaBytes: newQuotaBytes,
        }),
      });
      if (res.ok) {
        showToast("ルーム共通上限を変更しました", "success");
        return true;
      } else {
        const errorText = await res.text();
        showToast(`エラー: ${errorText}`, "error");
        return false;
      }
    } catch (err) {
      console.error(err);
      showToast("通信エラーが発生しました", "error");
      return false;
    }
  };

  const handleUpdateRoomQuota = async (
    roomId: number,
    newQuotaBytes: number,
  ): Promise<boolean> => {
    try {
      const res = await fetch(`/api/files/admin/room-quota?honbuId=${honbuId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hpcode: adminHpcode, roomId, newQuotaBytes }),
      });
      if (res.ok) {
        showToast("ルーム上限を変更しました", "success");
        return true;
      } else {
        const errorText = await res.text();
        showToast(`エラー: ${errorText}`, "error");
        return false;
      }
    } catch (err) {
      console.error(err);
      showToast("通信エラーが発生しました", "error");
      return false;
    }
  };

  // ブックマークデータ用
  const [bookmarkedItems, setBookmarkedItems] = useState<BookmarkedItemDto[]>(
    [],
  );
  const [isBookmarkedLoading, setIsBookmarkedLoading] = useState(false);

  // マイフォルダデータ用
  const [myFiles, setMyFiles] = useState<MyFileItemDto[]>([]);
  const [isMyFilesLoading, setIsMyFilesLoading] = useState(false);

  // --- ブックマーク一覧の取得 ---
  const fetchBookmarked = async () => {
    setIsBookmarkedLoading(true);
    try {
      const res = await fetch(`/api/files/bookmarked?honbuId=${honbuId}`);
      if (res.ok) {
        const data = await res.json();
        setBookmarkedItems(data.items);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsBookmarkedLoading(false);
    }
  };

  // --- 共有中のファイル一覧の取得 ---
  const fetchMyFiles = async () => {
    setIsMyFilesLoading(true);
    try {
      const res = await fetch(`/api/files/my-files?honbuId=${honbuId}`);
      if (res.ok) {
        const data = await res.json();
        setMyFiles(data.files);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsMyFilesLoading(false);
    }
  };

  // ビュー切り替え時の副作用
  useEffect(() => {
    if (currentView === "bookmarked") {
      fetchBookmarked();
    } else if (currentView === "trashShared") {
      fetchSharedTrash();
    } else if (currentView === "trashMyFolder") {
      fetchMyFolderTrash();
    } else if (currentView === "sharedFiles") {
      fetchMyFiles();
    } else if (currentView === "admin") {
      fetchManagedFacilities();
      fetchAdminData();
    }
  }, [currentView]); // eslint-disable-line react-hooks/exhaustive-deps

  // サイドバー操作
  const handleSidebarClick = (view: string) => {
    if (view === "部屋の共有フォルダ") {
      setCurrentView("files");
      setCurrentFolderId(null);
    } else if (view === "ゴミ箱（共有フォルダ）") {
      setCurrentView("trashShared");
    } else if (view === "ゴミ箱（マイフォルダ）") {
      setCurrentView("trashMyFolder");
    } else if (view === "ブックマーク") {
      setCurrentView("bookmarked");
    } else if (view === "マイフォルダ") {
      setCurrentView("myFolder");
      setMyFolderCurrentFolderId(null);
    } else if (view === "共有中のファイル") {
      setCurrentView("sharedFiles");
    } else if (view === "チャット送信ファイル") {
      setCurrentView("roomSendFiles");
    } else if (view === "施設管理") {
      setCurrentView("admin");
    }
  };

  // ブックマーク一覧からのファイルクリック（プレビューへ変換）
  const handleBookmarkedItemClick = (item: BookmarkedItemDto) => {
    // BookmarkedItemDto を FileItemDto の形式に変換してプレビューに渡す
    const fileDto: FileItemDto = {
      fileId: item.id,
      fileName: item.name,
      fileSize: item.size,
      contentType: "", // 必要ならAPIで返すか、拡張子から推定
      fileExtension: item.name.split(".").pop() || "",
      isLocked: false,
      isBookmarked: true,
      thumbnailStatus: item.thumbnailStatus,
      updatedAt: item.updatedAt,
      createdBy: 0, // 詳細不要
      uploaderName: "",
      uploaderDept: "",
    };
    handleFileClick(fileDto, item.roomId); // ブックマーク元のroomIdでプレビュー
  };

  // ブックマーク解除ハンドラ（一覧から削除）
  const handleRemoveBookmarkFromList = async (
    id: string,
    type: "folder" | "file",
  ) => {
    await handleToggleBookmark(id, type);
    fetchBookmarked();
  };

  // --- フォルダピッカー経由の移動・コピーハンドラ ---
  const handleOpenFolderPicker = (file: FileItemDto, mode: "move" | "copy") => {
    setFolderPickerFile(file);
    setFolderPickerMode(mode);
    setFolderPickerOpen(true);
  };

  const handleFolderPickerConfirm = async (
    targetRoomId: number,
    targetFolderId: string | null,
  ) => {
    if (!folderPickerFile) return;
    const file = folderPickerFile;
    setFolderPickerOpen(false);

    try {
      if (folderPickerMode === "move") {
        const params = new URLSearchParams({
          type: "file",
          targetRoomId: String(targetRoomId),
          honbuId: String(honbuId),
          roomId: String(roomId),
        });
        const res = await fetch(
          `/api/files/items/${file.fileId}/move?${params}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(targetFolderId),
          },
        );
        if (res.ok) {
          showToast("移動しました", "success");
          setIsDetailOpen(false);
          refresh();
          refreshMyFolder();
        } else {
          showToast(`移動失敗: ${await res.text()}`, "error");
        }
      } else {
        const params = new URLSearchParams({
          honbuId: String(honbuId),
          roomId: String(roomId),
          targetRoomId: String(targetRoomId),
        });
        if (targetFolderId) params.set("targetFolderId", targetFolderId);
        const res = await fetch(
          `/api/files/items/${file.fileId}/copy?${params}`,
          { method: "POST" },
        );
        if (res.ok) {
          showToast("コピーしました", "success");
          setIsDetailOpen(false);
          refresh();
          refreshMyFolder();
        } else {
          showToast(`コピー失敗: ${await res.text()}`, "error");
        }
      }
    } catch (err) {
      console.error(err);
      showToast("通信エラーが発生しました", "error");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        bgcolor: "background.default",
      }}
    >
      {/* ローディングオーバーレイ */}
      {isLoading && <LoadingOverlay color={palette} message="Now Loading..." />}

      {/* サイドバー: PC=常時表示, モバイル=Drawer */}
      {isMobile ? (
        <Drawer
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            zIndex: 1200,
            "& .MuiDrawer-paper": { width: 320, borderRadius: 0 },
          }}
        >
          <Sidebar
            storage={
              currentView === "myFolder" ? myFolderData?.storage : data?.storage
            }
            isAdmin={data?.isAdmin ?? false}
            roomId={roomId}
            roomName={data?.roomName ?? ""}
            activeItem={
              currentView === "files"
                ? "部屋の共有フォルダ"
                : currentView === "trashShared"
                  ? "ゴミ箱（共有フォルダ）"
                  : currentView === "trashMyFolder"
                    ? "ゴミ箱（マイフォルダ）"
                    : currentView === "bookmarked"
                      ? "ブックマーク"
                      : currentView === "myFolder"
                        ? "マイフォルダ"
                        : currentView === "sharedFiles"
                          ? "共有中のファイル"
                          : currentView === "admin"
                            ? "施設管理"
                            : ""
            }
            onItemClick={(view) => {
              handleSidebarClick(view);
              setMobileDrawerOpen(false);
            }}
          />
        </Drawer>
      ) : (
        <Sidebar
          storage={
            currentView === "myFolder" ? myFolderData?.storage : data?.storage
          }
          isAdmin={data?.isAdmin ?? false}
          roomId={roomId}
          roomName={data?.roomName ?? ""}
          activeItem={
            currentView === "files"
              ? "部屋の共有フォルダ"
              : currentView === "trashShared"
                ? "ゴミ箱（共有フォルダ）"
                : currentView === "trashMyFolder"
                  ? "ゴミ箱（マイフォルダ）"
                  : currentView === "bookmarked"
                    ? "ブックマーク"
                    : currentView === "myFolder"
                      ? "マイフォルダ"
                      : currentView === "sharedFiles"
                        ? "共有中のファイル"
                        : currentView === "roomSendFiles"
                          ? "チャット送信ファイル"
                          : currentView === "admin"
                            ? "施設管理"
                            : ""
          }
          onItemClick={handleSidebarClick}
        />
      )}

      {/* メインコンテンツ + ヘルプパネル（パネルモード）のラッパー */}
      <Box sx={{ flexGrow: 1, display: "flex", overflow: "hidden", minWidth: 0 }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minWidth: 0,
          pb: isMobile ? "56px" : 0,
        }}
      >
        {/* ビューの切り替え (モバイル時はgrid固定) */}
        {currentView === "admin" ? (
          <AdminContent
            data={adminData}
            roomData={adminRoomData}
            isLoading={isAdminLoading}
            palette={palette}
            facilities={managedFacilities}
            selectedHpcode={adminHpcode}
            onFacilityChange={handleAdminFacilityChange}
            onUpdateFacilityQuota={handleUpdateFacilityQuota}
            onUpdateUserQuota={handleUpdateUserQuota}
            onUpdateDefaultRoomQuota={handleUpdateDefaultRoomQuota}
            onUpdateRoomQuota={handleUpdateRoomQuota}
            onRefresh={() => fetchAdminData()}
          />
        ) : currentView === "bookmarked" ? (
          <BookmarkedContent
            items={bookmarkedItems}
            isLoading={isBookmarkedLoading}
            honbuId={honbuId}
            onRemoveBookmark={handleRemoveBookmarkFromList}
            onItemClick={handleBookmarkedItemClick}
            palette={palette}
            isMobile={isMobile}
          />
        ) : currentView === "trashShared" ? (
          <TrashContent
            title="ゴミ箱（共有フォルダ）"
            files={sharedTrashData?.files || []}
            folders={sharedTrashData?.folders || []}
            isLoading={isSharedTrashLoading}
            palette={palette}
            isMobile={isMobile}
            onRestore={handleTrashRestore}
            onHardDelete={handleTrashHardDelete}
            onBatchRestore={handleTrashBatchRestore}
            onBatchHardDelete={handleTrashBatchHardDelete}
          />
        ) : currentView === "trashMyFolder" ? (
          <TrashContent
            title="ゴミ箱（マイフォルダ）"
            files={myTrashData?.files || []}
            folders={myTrashData?.folders || []}
            isLoading={isMyTrashLoading}
            palette={palette}
            isMobile={isMobile}
            onRestore={handleTrashRestore}
            onHardDelete={handleTrashHardDelete}
            onBatchRestore={handleTrashBatchRestore}
            onBatchHardDelete={handleTrashBatchHardDelete}
          />
        ) : currentView === "roomSendFiles" ? (
          <RoomSendFilesContent roomId={roomId} honbuId={honbuId} palette={palette} />
        ) : currentView === "sharedFiles" ? (
          <SharedFilesContent
            files={myFiles}
            isLoading={isMyFilesLoading}
            honbuId={honbuId}
            palette={palette}
            onFileClick={(file) => {
              const fileDto: FileItemDto = {
                fileId: file.fileId,
                fileName: file.fileName,
                fileSize: file.fileSize,
                contentType: file.contentType || "",
                fileExtension: file.fileName.split(".").pop() || "",
                isLocked: false,
                isBookmarked: file.isBookmarked,
                thumbnailStatus: file.thumbnailStatus,
                updatedAt: file.updatedAt,
                createdBy: 0,
                uploaderName: "",
                uploaderDept: "",
              };
              handleFileClick(fileDto);
            }}
          />
        ) : currentView === "myFolder" ? (
          <MainContent
            hpcode={hpcode}
            effectiveRoomId={0}
            honbuId={honbuId}
            data={myFolderData}
            isLoading={isMyFolderLoading}
            viewMode={isMobile ? "grid" : viewMode}
            onViewModeChange={handleViewModeChange}
            onFolderClick={(id) => setMyFolderCurrentFolderId(id)}
            onFileClick={handleFileClick}
            onShowDetail={handleShowDetail}
            onRefresh={async () => refreshMyFolder()}
            onToggleBookmark={handleToggleBookmark}
            onUploadFiles={(files) => handleUploadFiles(files, true)}
            onUploadClick={() =>
              document.getElementById("file-input-myfolder")?.click()
            }
            onCreateFolderClick={openCreateFolderDialog}
            onDeleteItem={handleDeleteClick}
            onRenameClick={(id, name, type) => openRenameDialog(id, name, type)}
            onMoveFile={(fileId, targetFolderId) =>
              console.log("Move", fileId, targetFolderId)
            }
            onContextMenu={(e) => e.preventDefault()}
            isMobile={isMobile}
            isSelectionMode={isSelectionMode}
            selectedItems={selectedItems}
            onToggleSelection={handleToggleSelection}
            onEnterSelectionMode={handleEnterSelectionMode}
            onExitSelectionMode={handleExitSelectionMode}
            onBulkDownload={handleBulkDownload}
            onBulkDelete={handleBulkDelete}
          />
        ) : (
          <MainContent
            hpcode={hpcode}
            effectiveRoomId={roomId}
            honbuId={honbuId}
            data={data}
            isLoading={isLoading}
            viewMode={isMobile ? "grid" : viewMode}
            onViewModeChange={handleViewModeChange}
            onFolderClick={(id) => setCurrentFolderId(id)}
            onFileClick={handleFileClick}
            onShowDetail={handleShowDetail}
            onRefresh={async () => refresh()}
            onToggleBookmark={handleToggleBookmark}
            onUploadFiles={handleUploadFiles}
            onUploadClick={() =>
              document.getElementById("file-input-global")?.click()
            }
            onCreateFolderClick={openCreateFolderDialog}
            onDeleteItem={handleDeleteClick}
            onRenameClick={(id, name, type) => openRenameDialog(id, name, type)}
            onMoveFile={(fileId, targetFolderId) =>
              console.log("Move", fileId, targetFolderId)
            }
            onContextMenu={(e) => e.preventDefault()}
            isMobile={isMobile}
            isSelectionMode={isSelectionMode}
            selectedItems={selectedItems}
            onToggleSelection={handleToggleSelection}
            onEnterSelectionMode={handleEnterSelectionMode}
            onExitSelectionMode={handleExitSelectionMode}
            onBulkDownload={handleBulkDownload}
            onBulkDelete={handleBulkDelete}
            onOpenPermission={handleOpenPermissionDialog}
          />
        )}
      </Box>

      {/* ヘルプドロワー（パネルモード: 画面幅 1300px 以上） */}
      {isWide && (
        <HelpDrawer
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          paletteColor={palette}
          isDark={isDarkMode}
          variant="panel"
          honbuId={honbuId}
        />
      )}
      </Box>

      {/* 右詳細パネル: ファイル選択時のみ表示 */}
      {isDetailOpen && selectedDetailFile && (
        <DetailPanel
          file={selectedDetailFile}
          palette={palette}
          honbuId={honbuId}
          roomId={roomId}
          onClose={() => setIsDetailOpen(false)}
          onDelete={() => handleDeleteClick(selectedDetailFile.fileId, "file")}
          onRename={(file) =>
            openRenameDialog(file.fileId, file.fileName, "file")
          }
          onCopy={(file) => handleCopyFile(file.fileId)}
          onToggleBookmark={(file) => handleToggleBookmark(file.fileId, "file")}
          onMoveToFolder={(file) => handleOpenFolderPicker(file, "move")}
          onCopyToFolder={(file) => handleOpenFolderPicker(file, "copy")}
        />
      )}

      {/* ヘルプドロワー（オーバーレイモード: 画面幅 1300px 未満） */}
      {!isWide && (
        <HelpDrawer
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          paletteColor={palette}
          isDark={isDarkMode}
          variant="overlay"
          honbuId={honbuId}
        />
      )}

      {/* プレビュー (FilePreviewer) - 新規追加 */}
      <FilePreviewer
        file={previewFile}
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        honbuId={honbuId}
        roomId={previewRoomId}
        palette={palette}
      />

      {/* --- 権限設定ダイアログ --- */}
      {permissionTarget && (
        <PermissionDialog
          open={permissionDialogOpen}
          onClose={() => setPermissionDialogOpen(false)}
          folderId={permissionTarget.folderId}
          folderName={permissionTarget.folderName}
          isRestricted={permissionTarget.isRestricted}
          honbuId={honbuId}
          roomId={roomId}
          palette={palette}
          onToggleRestriction={handleToggleRestriction}
          onRefresh={() => {
            if (currentView === "myFolder") {
              refreshMyFolder();
            } else {
              refresh();
            }
          }}
          showToast={showToast}
        />
      )}

      {/* --- フォルダ選択ダイアログ --- */}
      <FolderPickerDialog
        open={folderPickerOpen}
        mode={folderPickerMode}
        palette={palette}
        hpcode={hpcode}
        roomId={roomId}
        honbuId={honbuId}
        isMyFolderView={currentView === "myFolder"}
        onClose={() => setFolderPickerOpen(false)}
        onConfirm={handleFolderPickerConfirm}
      />

      {/* --- 削除確認ダイアログ --- */}
      <SimpleAlertDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={executeDelete}
        title="削除の確認"
        message="本当にゴミ箱へ移動しますか？この操作は元に戻せます。"
        confirmLabel="削除"
        severity="warning"
      />

      {/* --- 一括削除確認ダイアログ --- */}
      <SimpleAlertDialog
        open={bulkDeleteConfirmOpen}
        onClose={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={executeBulkDelete}
        title="一括削除の確認"
        message={`${selectedItems.length}件のアイテムをゴミ箱へ移動しますか？`}
        confirmLabel="削除"
        severity="warning"
      />

      {/* --- 追加: 右下の通知トースト (3秒で消える) --- */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000} // 3秒
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%", boxShadow: 3 }}
          variant="filled" // 目立つようにfilled推奨
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* アップロード進捗ウィジェット */}
      {uploadProgress.visible && (
        <UploadProgressWidget
          total={uploadProgress.total}
          current={uploadProgress.current}
          onClose={() =>
            setUploadProgress((prev) => ({ ...prev, visible: false }))
          }
          bottom={isMobile ? 144 : 24}
        />
      )}

      {/* モバイル用: フッターナビゲーション */}
      {isMobile && (
        <BottomNavigation
          value={
            roomId > 0
              ? currentView === "files"
                ? 1
                : currentView === "roomSendFiles"
                  ? 2
                  : -1
              : currentView === "myFolder"
                ? 1
                : currentView === "bookmarked"
                  ? 2
                  : -1
          }
          onChange={(_, newValue) => {
            if (newValue === 0) {
              setMobileDrawerOpen(true);
            } else if (newValue === 1) {
              handleSidebarClick(
                roomId > 0 ? "部屋の共有フォルダ" : "マイフォルダ",
              );
            } else if (newValue === 2) {
              handleSidebarClick(
                roomId > 0 ? "チャット送信ファイル" : "ブックマーク",
              );
            }
          }}
          showLabels
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <BottomNavigationAction
            label="メニュー"
            icon={<MenuIcon sx={{ fontSize: 22 }} />}
          />
          <BottomNavigationAction
            label={roomId > 0 ? "共有フォルダ" : "マイフォルダ"}
            icon={
              roomId > 0 ? (
                <FolderIcon sx={{ fontSize: 22 }} />
              ) : (
                <ContactIcon sx={{ fontSize: 22 }} />
              )
            }
          />
          <BottomNavigationAction
            label={roomId > 0 ? "送信ファイル" : "ブックマーク"}
            icon={
              roomId > 0 ? (
                <DraftIcon sx={{ fontSize: 22 }} />
              ) : (
                <BookmarkIcon sx={{ fontSize: 22 }} />
              )
            }
          />
        </BottomNavigation>
      )}

      {/* モバイル用: 右下FAB (アップロード + フォルダ作成) */}
      {isMobile &&
        (currentView === "files" || currentView === "myFolder") &&
        !isSelectionMode && (
          <>
            <Fab
              color="primary"
              onClick={(e) => setFabAnchorEl(e.currentTarget)}
              sx={{
                position: "fixed",
                bottom: 72,
                right: 20,
                zIndex: 1100,
                bgcolor: palette ? PaletteColorHex[palette] : "primary.main",
                "&:hover": {
                  bgcolor: palette ? PaletteColorHex[palette] : "primary.dark",
                  filter: "brightness(0.9)",
                },
              }}
            >
              <AddIcon />
            </Fab>
            <Menu
              anchorEl={fabAnchorEl}
              open={isFabMenuOpen}
              onClose={() => setFabAnchorEl(null)}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              transformOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <MenuItem
                component="label"
                htmlFor={
                  currentView === "myFolder"
                    ? "file-input-myfolder"
                    : "file-input-global"
                }
                onClick={() => setFabAnchorEl(null)}
              >
                <ListItemIcon>
                  <UploadIcon fontSize="small" />
                </ListItemIcon>
                アップロード
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setFabAnchorEl(null);
                  openCreateFolderDialog();
                }}
              >
                <ListItemIcon>
                  <NewFolderIcon fontSize="small" />
                </ListItemIcon>
                新しいフォルダー
              </MenuItem>
            </Menu>
          </>
        )}

      {/* 非表示のファイル選択Input */}
      <input
        id="file-input-global"
        type="file"
        multiple
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          opacity: 0,
          overflow: "hidden",
        }}
        onChange={(e) => {
          if (e.target.files) handleUploadFiles(Array.from(e.target.files));
          e.target.value = "";
        }}
      />
      <input
        id="file-input-myfolder"
        type="file"
        multiple
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          opacity: 0,
          overflow: "hidden",
        }}
        onChange={(e) => {
          if (e.target.files)
            handleUploadFiles(Array.from(e.target.files), true);
          e.target.value = "";
        }}
      />
      <Dialog
        open={isInputDialogOpen}
        onClose={() => setIsInputDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        slotProps={{ paper: { sx: { borderRadius: 2, overflow: "hidden" } } }}
      >
        <Box
          sx={{
            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`,
            color: "#fff",
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "3px solid #fff",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {inputMode === "createFolder" ? "新規フォルダ作成" : "名前の変更"}
          </Typography>
          <IconButton
            onClick={() => setIsInputDialogOpen(false)}
            size="small"
            sx={{ color: "#fff" }}
          >
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ height: 3, bgcolor: theme.palette.primary.main }} />
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="名前"
            fullWidth
            variant="outlined"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsInputDialogOpen(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              bgcolor: palette ? PaletteColorHex[palette] : "primary.main",
            }}
          >
            確定
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FileManager;
